import { listRelationshipCategories, listRelationshipSubtypesForScope, type DiagramType } from '../../../lib/diagram/registries';
import type { RelationshipCategoryManifest, RelationshipSubtypeManifest } from '../../../lib/diagram/relationship-manifest.types';

export type Direction = 'top' | 'right' | 'bottom' | 'left';

export interface SuggestionContext {
  diagramType: DiagramType;
  relationshipScope?: string;
  sourceNodeId: string;
  direction: Direction;
  existingEdgeSubtypeIds?: string[];
}

export interface RankedCategory {
  category: RelationshipCategoryManifest;
  score: number;
}

export interface RankedSubtype {
  subtype: RelationshipSubtypeManifest;
  score: number;
}

const mru: Map<string, number> = new Map();
const HIDDEN_GENOGRAM_SUBTYPE_IDS = new Set(['divorce', 'cohabitation', 'close']);

function isVisibleSubtype(diagramType: DiagramType, subtypeId: string): boolean {
  return diagramType !== 'genogram' || !HIDDEN_GENOGRAM_SUBTYPE_IDS.has(subtypeId);
}

function getMRUScore(subtypeId: string): number {
  return mru.get(subtypeId) ?? 0;
}

export function recordSelection(subtypeId: string) {
  mru.set(subtypeId, (mru.get(subtypeId) ?? 0) + 1);
}

export function rankCategories(ctx: SuggestionContext): RankedCategory[] {
  const cats = listRelationshipCategories(ctx.diagramType);
  // Simple: preserve manifest order; could add domain-specific boosts later
  return cats.map((c, idx) => ({ category: c, score: 100 - idx }));
}

export function rankSubtypes(ctx: SuggestionContext, categoryId: string): RankedSubtype[] {
  const subs = listRelationshipSubtypesForScope(ctx.diagramType, categoryId, ctx.relationshipScope).filter((s) => isVisibleSubtype(ctx.diagramType, s.id));
  const dir = ctx.direction;
  return subs
    .map((s) => {
      const base = s.basePriority ?? 0;
      const w = s.directionWeights?.[dir] ?? 0;
      const recent = getMRUScore(s.id) * 2;
      const penalty = (ctx.existingEdgeSubtypeIds || []).includes(s.id) ? -5 : 0;
      return { subtype: s, score: base * 10 + w * 10 + recent + penalty };
    })
    .sort((a, b) => b.score - a.score);
}

export function resolveSearch(
  ctx: SuggestionContext,
  query: string
): RankedSubtype[] {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  const allCats = listRelationshipCategories(ctx.diagramType);
  const results: RankedSubtype[] = [];
  for (const c of allCats) {
    for (const r of rankSubtypes(ctx, c.id)) {
      const hay = [r.subtype.label.toLowerCase(), ...(r.subtype.synonyms || []).map((s) => s.toLowerCase())].join(' ');
      const hit = tokens.every((t) => hay.includes(t));
      if (hit) results.push(r);
    }
  }
  // If none matched, fall back to direction-ranked top subtypes across categories
  if (results.length === 0) {
    for (const c of allCats) {
      results.push(...rankSubtypes(ctx, c.id).slice(0, 3));
    }
  }
  // De-duplicate by subtype id keeping best score
  const bestById = new Map<string, RankedSubtype>();
  for (const r of results) {
    const prev = bestById.get(r.subtype.id);
    if (!prev || r.score > prev.score) bestById.set(r.subtype.id, r);
  }
  return Array.from(bestById.values()).sort((a, b) => b.score - a.score);
}
